import { useCallback, useEffect, useRef } from 'react'

import { AnalyticsService } from '../../../../services/analytics'
import { bookingFlowVariant } from '../../../../lib/booking-flow'
import { BOOK_FIELDS, BOOK_FIELDS_V3, BOOK_SECTIONS, BOOK_SECTIONS_V3, createBookingProgress, halfVisible, sectionIndex } from '../../../../lib/booking-progress'
import { formVersion } from '../../../../lib/form-version'

const SECTION_ATTR = 'data-book-section'
const STICKY_BAR = '[data-testid="mobile-bar-book"]'
const FORM_ID = 'booking-step-form'
// Where a payment that went through lands: the wallet path's finish step and
// the Stripe redirect's success page.
const COMPLETED_PATH = /^\/(booking\/finish|checkout\/success)/

// The form's field names -> the analytics keys of BOOK_FIELDS (plus notes).
const FIELD_KEY = { carSize: 'car_size', date: 'date', timeSlot: 'time_slot', carLocation: 'car_location', postcode: 'postcode', address: 'house_number', mobile: 'mobile', notes: 'notes' }
export const progressField = name => FIELD_KEY[name] || null

const scrollPct = () => {
  const doc = document.documentElement
  const height = Math.max(doc.scrollHeight, document.body ? document.body.scrollHeight : 0)
  return height > 0 ? ((window.scrollY + window.innerHeight) / height) * 100 : 0
}

// Progress through Book & pay, top to bottom (ANALYTICS.md, "Progress
// through Book & pay"): booking_section_viewed once per section per page
// view, and one booking_form_exit when the visitor leaves without paying.
// The bookkeeping is lib/booking-progress.js; this is the DOM side.
//
// getFieldValues() -> { car_size, date, time_slot, postcode, house_number,
// mobile }, read when the visitor leaves. Returns onBeforePay wrapped, so
// every pay press (and whether it was refused) is counted, and
// noteEngaged(name) / noteSource(name, source) (form field names) for where
// each value came from (fields_filled, fill_source).
export const useBookingProgress = ({ getFieldValues, onBeforePay }) => {
  const progressRef = useRef(null)
  const sectionsRef = useRef(null)
  if (!progressRef.current) {
    const formVariant = bookingFlowVariant()
    const v3 = formVersion() === 'v3'
    sectionsRef.current = v3 ? BOOK_SECTIONS_V3 : BOOK_SECTIONS
    progressRef.current = createBookingProgress({
      sections: sectionsRef.current,
      fields: v3 ? BOOK_FIELDS_V3 : BOOK_FIELDS,
      onSection: ({ section, section_index: index }) => AnalyticsService.trackBookingSectionViewed(section, index, formVariant),
      onExit: props => AnalyticsService.trackBookingFormExit({ ...props, form_variant: formVariant })
    })
  }
  const valuesRef = useRef(getFieldValues)
  valuesRef.current = getFieldValues

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined
    const progress = progressRef.current
    const leave = trigger => {
      let fieldValues = {}
      try { fieldValues = valuesRef.current ? valuesRef.current() : {} } catch (e) { /* never break leaving */ }
      progress.exit(trigger, { fieldValues, viewportHeight: window.innerHeight })
    }

    // One observer for every section. Sections that render later (the notes
    // box, the pay buttons once Stripe has answered) are picked up by
    // watching the form for new nodes.
    let io = null
    let mo = null
    let rescanTimer = null
    const observed = new WeakSet()
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(entries => {
        entries
          .filter(entry => halfVisible(entry, window.innerHeight))
          .map(entry => entry.target.getAttribute(SECTION_ATTR))
          .sort((a, b) => sectionIndex(a, sectionsRef.current) - sectionIndex(b, sectionsRef.current))
          .forEach(name => progress.sectionSeen(name))
      }, { threshold: [0, 0.25, 0.5, 0.75, 1] })
      const scan = () => {
        rescanTimer = null
        document.querySelectorAll(`[${SECTION_ATTR}]`).forEach(el => {
          if (observed.has(el) || !sectionsRef.current.includes(el.getAttribute(SECTION_ATTR))) return
          observed.add(el)
          io.observe(el)
        })
      }
      scan()
      const root = document.getElementById(FORM_ID)
      if (root && typeof MutationObserver !== 'undefined') {
        mo = new MutationObserver(() => { if (!rescanTimer) rescanTimer = setTimeout(scan, 200) })
        mo.observe(root, { childList: true, subtree: true })
      }
    }

    // At most every 200ms, and once more when scrolling stops: without the
    // trailing read a quick fling to the bottom ended inside the 200ms and
    // was never counted (max_scroll_pct 94 for a visitor at the bottom).
    let lastScroll = 0
    let trailing = null
    const onScroll = () => {
      const t = Date.now()
      clearTimeout(trailing)
      trailing = setTimeout(() => progress.noteScroll(scrollPct()), 220)
      if (t - lastScroll < 200) return
      lastScroll = t
      progress.noteScroll(scrollPct())
    }
    progress.noteScroll(scrollPct())
    const onClick = e => {
      if (e.target && typeof e.target.closest === 'function' && e.target.closest(STICKY_BAR)) progress.noteStickyBar()
    }
    // Back on the tab after a 'hidden' exit: the page view goes on, and the
    // next hide sends another exit (exit_seq 2, 3...). Before 11 Oct a
    // visitor who switched app and came back to type was never counted.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') leave('hidden')
      else progress.reopen()
    }
    const onPageHide = () => leave('pagehide')
    const onPageShow = e => { if (e.persisted) { progress.resume(); progress.reopen() } }

    // Typed or autofilled: every input event in the text boxes, with how
    // many characters it moved and whether the box had the focus.
    const lengths = {}
    const onFocusIn = e => {
      const key = e.target && progressField(e.target.name)
      if (key && typeof e.target.value === 'string') lengths[key] = e.target.value.length
    }
    const onInput = e => {
      const el = e.target
      const key = el && progressField(el.name)
      if (!key || typeof el.value !== 'string') return
      const before = lengths[key] || 0
      lengths[key] = el.value.length
      progress.noteInput(key, { focused: document.activeElement === el, delta: el.value.length - before, inputType: e.inputType })
    }
    const form = document.getElementById(FORM_ID)
    if (form) {
      form.addEventListener('focusin', onFocusIn)
      form.addEventListener('input', onInput)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    document.addEventListener('click', onClick, true)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('pageshow', onPageShow)
    return () => {
      if (form) {
        form.removeEventListener('focusin', onFocusIn)
        form.removeEventListener('input', onInput)
      }
      window.removeEventListener('scroll', onScroll)
      clearTimeout(trailing)
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
      window.removeEventListener('pageshow', onPageShow)
      clearTimeout(rescanTimer)
      if (mo) mo.disconnect()
      if (io) io.disconnect()
      // Unmounted by a route change: the router has already moved, so the
      // address bar says where to.
      const path = window.location.pathname
      if (path.startsWith('/booking/book')) return
      leave(COMPLETED_PATH.test(path) ? 'completed' : 'route')
    }
  }, [])

  const trackedBeforePay = useCallback(async kind => {
    const ok = onBeforePay ? await onBeforePay(kind) : true
    progressRef.current.notePay(!!ok)
    return ok
  }, [onBeforePay])
  const noteEngaged = useCallback(name => progressRef.current.noteEngaged(progressField(name)), [])
  const noteSource = useCallback((name, source) => progressRef.current.noteSource(progressField(name), source), [])
  return { trackedBeforePay, noteEngaged, noteSource }
}
